/**
 * The identity pick (ui-map-interface.md §2.5, R3293).
 *
 * The map canvas has two identities, dark and light (§2.1). A generated
 * map ink of `gen/presentation.ts` and `gen/balance.ts` is
 * `(LIGHT ? light : dark)`, so the pick must stand before any module
 * copies an ink into a constant. This module evaluates once, when the
 * first generated module imports it, and a change of the identity
 * reloads the page (the owner's ruling: a reload, not a live flip). The
 * render loop therefore pays nothing for the second identity (§10).
 *
 * The identity of the page is:
 *
 *   1. dark on every page whose root does not carry
 *      `data-identity-scope="map"`: the hub pages, the labs and the sea
 *      view stay dark;
 *   2. otherwise the `identity` knob of the location hash
 *      (`#identity=light` or `#identity=dark`), a tool of the developer;
 *   3. otherwise the `identity` field of the stored settings blob;
 *   4. otherwise dark.
 *
 * `pickIdentityFrom` is pure, so the rule is testable with no browser.
 * This module imports nothing that imports a generated module: the
 * generated modules import it.
 */

import { readStore } from '../ui/localstore';
import { SETTINGS_KEY } from '../ui/settingskey';

/** The two identities of the map canvas, the default first. */
export const IDENTITIES = ['dark', 'light'] as const;
export type Identity = (typeof IDENTITIES)[number];

/** The attribute of the root element that puts a page in the scope of
 *  the pick, and the value it takes. */
export const IDENTITY_SCOPE_ATTR = 'data-identity-scope';
export const IDENTITY_SCOPE_MAP = 'map';

/** An identity from a value of any type, or null. */
export function identityOf(v: unknown): Identity | null {
  return v === 'dark' || v === 'light' ? v : null;
}

/** The `identity` knob of a location hash, or null. The pairs follow
 *  the form of `fast/knobs.ts`: a space, `&`, `,` or `;` separates them. */
export function identityFromHash(hash: string): Identity | null {
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const raw of body.split(/(?:[\s&,;]|%20)+/i)) {
    const eq = raw.indexOf('=');
    if (eq > 0 && raw.slice(0, eq).toLowerCase() === 'identity') {
      return identityOf(raw.slice(eq + 1).toLowerCase());
    }
  }
  return null;
}

/** Picks the identity from values a caller states. */
export function pickIdentityFrom(scoped: boolean, hash: string, stored: unknown): Identity {
  if (!scoped) return 'dark';
  return identityFromHash(hash) ?? identityOf(stored) ?? 'dark';
}

/** Whether the root of this page carries the map scope. */
function pageScoped(): boolean {
  try {
    return globalThis.document?.documentElement?.getAttribute(IDENTITY_SCOPE_ATTR) === IDENTITY_SCOPE_MAP;
  } catch {
    return false;
  }
}

/** The `identity` field of the stored settings blob, or null. */
function storedIdentity(): unknown {
  const raw = readStore(SETTINGS_KEY);
  if (!raw) return null;
  try {
    const obj: unknown = JSON.parse(raw);
    return typeof obj === 'object' && obj !== null ? (obj as { identity?: unknown }).identity : null;
  } catch {
    return null;
  }
}

/** The hash of this page, or '' where there is none. */
function pageHash(): string {
  try {
    return globalThis.location?.hash ?? '';
  } catch {
    return '';
  }
}

/** The identity this page would pick with `stored` as the settings
 *  field: the scope and the hash knob of this page still rule. The
 *  account copy of the settings reads it to know whether a reload is due. */
export function pageIdentity(stored: unknown): Identity {
  return pickIdentityFrom(pageScoped(), pageHash(), stored);
}

/** The identity of this page, picked once. */
export const IDENTITY: Identity = pickIdentityFrom(pageScoped(), pageHash(), storedIdentity());

/** True on a page in the light identity. Every generated map ink reads it. */
export const LIGHT: boolean = IDENTITY === 'light';
